import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';
import Dialog from 'ui/dialog';

import { getIconPath } from 'common';
import Store from 'store';
import EditingContext from 'store/editingContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTimes, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import Tooltip from 'ui/tooltip';
import { WithEngravingTooltip } from 'tooltip';

import 'components/EngravingView.scss';
import { searchRegex } from 'utils';
import { underlinerFunc } from 'ui/select';
import BlockClearButton, { builderHasEngravings, builderHasPaperdoll, builderSelectEngravings } from './common';

function EngravingButton({ id, onSelect, disabled, search }) {
  const data = Data.engraving[id];
  return (
    <WithEngravingTooltip id={id}>
      <li className={classNames("lap-EngravingButton", {"lap-disabled": disabled})}>
        <div className="lap-name"/>
        <div className="lap-name">{underlinerFunc(data.name, search)}</div>
        <div className="lap-EngravingIcon" style={{backgroundImage: `url(${getIconPath(data.icon)})`}} onClick={disabled ? null : (() => onSelect(id))}>
          <div className="lap-engraving-frame"/>
        </div>
      </li>
    </WithEngravingTooltip>
  );
}

function EngravingPicker({ value, onSelect, onClose, charClass }) {
  const [search, setSearch] = React.useState('');
  const onSetSearch = React.useCallback(e => setSearch(e.target.value), []);
  const onClearSearch = React.useCallback(() => setSearch(''), []);
  const inferno = Store.useBuilder(builder => builder.category === 'inferno');

  const searchRe = search.trim() && searchRegex(search.trim(), true);
  const engravings = React.useMemo(() => {
    const engravings = new Set();
    for (const group of Data.itemRandom['103#4']) {
      for (const sub of group.subgroups) {
        for (let i = 0; i < sub.options.length; i += 6) {
          const [type, id] = sub.options.slice(i, i + 6);
          if (type === 3) engravings.add(id);
        }
      }
    }
    const listClass = [], listBattle = [], listMalus = [];
    for (const id of engravings) {
      const data = Data.engraving[id];
      if (!data || !data.name) continue;
      if (charClass && data.class && charClass !== data.class) continue;
      if (searchRe && !data.name.match(searchRe)) continue;
      if (data.penalty) {
        if (!inferno) listMalus.push(id);
      } else if (data.class) {
        listClass.push(id);
      } else {
        listBattle.push(id);
      }
    }
    const engravingCmp = (a, b) => Data.engraving[a].name.localeCompare(Data.engraving[b].name);
    return [
      {id: 'class', list: listClass.sort(engravingCmp), name: 'Class Engravings'},
      {id: 'battle', list: listBattle.sort(engravingCmp), name: 'Battle Engravings'},
      {id: 'malus', list: listMalus.sort(engravingCmp), name: 'Malus Engravings'},
    ].filter(opt => opt.list.length);
  }, [searchRe, charClass]);
  const used = Store.useBuilder(builder => builder.engravings);
  return (
    <Dialog className="lap-EngravingPickerDialog" title={`Choose Engraving`} modal onClose={onClose}>
      <Dialog.Content>
        <div className="lap-engraving-search">
          <div className="lap-search">
            <input placeholder='Search' value={search} onChange={onSetSearch}/>
            {!!search.trim() && <FontAwesomeIcon icon={faTimesCircle} className="lap-clear" onClick={onClearSearch}/>}
          </div>
        </div>
        <div className="lap-engraving-options">
          {engravings.map(opt => (
            <React.Fragment key={opt.id}>
              <h3>{opt.name}</h3>
              <ul>
                {opt.list.map(id => <EngravingButton key={id} id={id} onSelect={onSelect} disabled={id !== value && used?.[id]} search={searchRe}/>)}
              </ul>
            </React.Fragment>
          ))}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

function BuilderEngraving({ id, value, editing, charClass }) {
  const inferno = Store.useBuilder(builder => builder.category === 'inferno');
  const [picking, setPicking] = React.useState(false);
  const dispatch = Store.useDispatch();
  const onChangeId = React.useCallback(newId => dispatch.setBuilder(builder => {
    builder = {...builder};
    builder.engravings = {...builder.engravings};
    const value = builder.engravings[id] || 3;
    delete builder.engravings[id];
    if (newId) builder.engravings[newId] = value;
    return builder;
  }), [dispatch, id]);
  const onChangeValue = React.useCallback(value => dispatch.setBuilder(builder => {
    if (inferno) return builder;
    builder = {...builder};
    builder.engravings = {...builder.engravings};
    builder.engravings[id] = value;
    return builder;
  }), [dispatch, id, inferno]);

  const onSelect = React.useCallback(value => {
    setPicking(false);
    onChangeId(value);
  }, [onChangeId]);

  const picker = !!picking && <EngravingPicker value={id} onSelect={onSelect} onClose={() => setPicking(false)} charClass={charClass}/>;

  const data = Data.engraving[id];
  if (!data) {
    if (!editing) return null;
    return <>
      <li className="lap-engraving lap-level-0" onClick={() => setPicking(true)}>
        <div className="lap-EngravingIcon lap-empty">
          <div className="lap-engraving-frame"/>
        </div>
        <div className="lap-engraving-details">
          <div className="lap-level">Select engraving...</div>
          <div className="lap-diamonds">
            <div/><div/><div/>
          </div>
        </div>
      </li>
      {picker}
    </>;
  }
  return <>
    <WithEngravingTooltip id={id} level={value}>
      <li className={classNames(`lap-engraving lap-level-${value}`, {"lap-malus": data.penalty, "lap-read-only": !editing, "lap-inferno": inferno})}>
        <div className="lap-EngravingIcon" style={{backgroundImage: `url(${getIconPath(data.icon)})`}} onClick={editing ? (() => setPicking(true)) : null}>
          <div className={`lap-engraving-frame lap-level-${value}`}>
            {value >= 3 && <span>{_F('sys.engrave.target_engrave_level_max')}</span>}
          </div>
        </div>
        <div className="lap-engraving-details">
          <div className="lap-level">
            {_F('sys.engrave.target_engrave_level', value, data.name)}
            {!!editing && <FontAwesomeIcon icon={faTimes} className="lap-remove" onClick={() => onChangeId(null)}/>}
          </div>
          <div className="lap-diamonds">
            {[1, 2, 3].map(lvl => <div key={lvl} className={value >= lvl ? "lap-active" : null} onClick={editing ? (() => onChangeValue(lvl)) : null}/>)}
          </div>
        </div>
      </li>
    </WithEngravingTooltip>
    {picker}
  </>;
}

export default function BuilderEngravings() {
  const editing = EditingContext.useStateView("builderEditing");

  const builder = Store.useBuilder();

  const locked = builderHasPaperdoll(builder);

  const engravings = React.useMemo(() => builderSelectEngravings(builder), [builder]);
  const onClear = React.useCallback(builder => ({...builder, engravings: {}}), []);

  const sorted = Object.entries(engravings).map(([id, value]) => ({id: parseInt(id), value})).sort((a, b) => {
    const ea = Data.engraving[a.id], eb = Data.engraving[b.id];
    if ((!ea?.penalty) !== (!eb?.penalty)) return ea?.penalty ? 1 : -1;
    if (a.value !== b.value) return b.value - a.value;
    return builder.category === 'inferno' ? ea.name.localeCompare(eb.name) : a.id - b.id;
  });

  return (
    <div className="lap-BuilderBlock lap-BuilderEngravings">
      <div className="lap-BuilderBlockHeader">
        Engravings
        {!!(locked && editing) && (
          <Tooltip tooltip="When the Items block is shown, Engravings are determined by equipped accessories.">
            <FontAwesomeIcon className="lap-notice" icon={faLock}/>  
          </Tooltip>
        )}
        {!!(!locked && editing && builderHasEngravings(builder)) && <BlockClearButton onClear={onClear}/>}
      </div>
      <ul className="lap-engravings">
        {sorted.map(({id, value}) => <BuilderEngraving key={id} id={id} value={value} editing={editing && !locked} charClass={builder.class}/>)}
        {!!(editing && !locked && (builder.category !== 'inferno' || sorted.length < 5)) && <BuilderEngraving editing={true} charClass={builder.class}/>}
      </ul>
    </div>
  );
}
